import { useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { exportBackup } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function DeleteWithBackupDialog({ 
  open, 
  onOpenChange, 
  title, 
  description, 
  itemCount,
  onConfirmDelete,
  leavePath = "/dashboard"
}) {
  const [backingUp, setBackingUp] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleBackupAndDelete = async () => {
    try {
      // First do the backup
      setBackingUp(true);
      const response = await exportBackup();
      
      // Create download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `charleroi_backup_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Backup downloaded successfully");
      setBackingUp(false);
      
      // Then delete
      setDeleting(true);
      await onConfirmDelete();
      setDeleting(false);
      onOpenChange(false);
      
    } catch (error) {
      console.error("Backup/delete error:", error);
      toast.error("Failed to complete operation");
      setBackingUp(false);
      setDeleting(false);
    }
  };

  const handleLeave = () => {
    onOpenChange(false);
    navigate(leavePath);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-white dark:bg-[#1e293b] max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
            <AlertTriangle className="text-red-600" size={20} />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600 dark:text-gray-300">
            {description}
            {itemCount !== undefined && (
              <span className="block mt-2 font-semibold text-red-600">
                {itemCount.toLocaleString()} record(s) will be deleted.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <div className="flex items-start gap-3">
              <Download className="text-amber-600 dark:text-amber-400 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-300">
                  Backup First Before Delete
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Clicking OK will automatically download a full system backup before deleting.
                </p>
              </div>
            </div>
          </div>
        </div>

        <AlertDialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleLeave}
            className="border-gray-300 dark:border-gray-600"
            data-testid="delete-dialog-leave-btn"
          >
            <X size={16} className="mr-2" />
            Leave
          </Button>
          <Button
            onClick={handleBackupAndDelete}
            disabled={backingUp || deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-dialog-ok-btn"
          >
            {backingUp ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Backing up...
              </>
            ) : deleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "OK"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
